// Security Plus activity log: what one browser-reported event contains.
// Events are client-reported (never proof of who typed a PIN); they carry the
// logged-in account and the PIN profile (initials) separately and never PIN
// digits, hashes, tokens, guest contact data or free-text comments.

export type ActivityCategory =
  | 'authorization'
  | 'view'
  | 'print'
  | 'override'
  | 'form'
  | 'change';

/** `unknown`: the write failed in a way that may have applied part of it. */
export type ActivityOutcome = 'succeeded' | 'failed' | 'cancelled' | 'partial' | 'unknown';

export type AuthorizationMethod = 'pin_entered' | 'pin_session' | 'admin_exemption';

/** restaurants/{restaurantId}/securityActivity/{eventId} */
export const ACTIVITY_COLLECTION = 'securityActivity';
export const RETENTION_DAYS = 90;

export const TARGET_TYPES = ['reservation', 'shift', 'table', 'space', 'regularTable', 'day'] as const;

export type ActivityTarget = {
  type: typeof TARGET_TYPES[number];
  id: string;
  outcome?: 'succeeded' | 'failed';
};

/** The only fields whose before/after values may be logged. */
export const CHANGE_FIELDS = [
  'guests',
  'date',
  'time',
  'duration',
  'tables',
  'blocked',
  'blockInterval',
] as const;

export type ChangeField = typeof CHANGE_FIELDS[number];

export type ActivityValue = string | number | boolean | null | string[];

export type ActivityChange = {
  field: ChangeField;
  before: ActivityValue;
  after: ActivityValue;
};

export type ActivityDetails = {
  targets?: ActivityTarget[];
  changes?: ActivityChange[];
  reason?: string;
};

export type DeviceInfo = { browser: string; os: string; appVersion: string };

export type ActivityEvent = {
  eventId: string;
  restaurantId: string;
  accountUid: string;
  category: ActivityCategory;
  action: string;
  permission: string | null;
  outcome: ActivityOutcome;
  reason: string | null;
  method: AuthorizationMethod | null;
  pinProfile: string | null;
  pinSessionId: string | null;
  authorizationId: string | null;
  targets: ActivityTarget[];
  changes: ActivityChange[];
  installationId: string;
  device: DeviceInfo;
  /** Browser clock, unconfirmed; the server stores its own receipt time. */
  clientTime: number;
};

/** Stored targets per event (the last slot says how many more there were). */
export const MAX_TARGETS = 30;
const MAX_QUEUED_TARGETS = 200;
const MAX_TEXT = 40;
const MAX_LIST = 30;
const MAX_STORED_VALUE = 120;

/** 32 hex characters; random, so a resend of the same event keeps its id. */
export const randomId = () => {
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else bytes.forEach((_, i) => (bytes[i] = Math.floor(Math.random() * 256)));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const text = (value: unknown, max = MAX_TEXT) =>
  typeof value === 'string' ? value.slice(0, max) : null;

const loggableValue = (value: unknown): ActivityValue => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.slice(0, MAX_TEXT);
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
      .slice(0, MAX_LIST)
      .map((v) => String(v).slice(0, MAX_TEXT));
  }
  return null;
};

const sameValue = (a: ActivityValue, b: ActivityValue) =>
  JSON.stringify(a) === JSON.stringify(b);

/** Before/after pairs of the allowlisted fields that actually changed. */
export const changesBetween = (
  before: Partial<Record<ChangeField, unknown>>,
  after: Partial<Record<ChangeField, unknown>>
): ActivityChange[] =>
  CHANGE_FIELDS.filter((field) => field in after)
    .map((field) => ({
      field,
      before: loggableValue(before[field]),
      after: loggableValue(after[field]),
    }))
    .filter((change) => !sameValue(change.before, change.after));

/** Reservation fields as logged; only those present in the written patch. */
const RESERVATION_FIELDS: [ChangeField, string][] = [
  ['guests', 'guests'],
  ['date', 'date'],
  ['time', 'time'],
  ['duration', 'reservationLength'],
  ['tables', 'tables'],
];

export const reservationChanges = (before: Record<string, any> = {}, patch: Record<string, any> = {}) => {
  const written = RESERVATION_FIELDS.filter(([, key]) => key in patch);
  return changesBetween(
    Object.fromEntries(written.map(([field, key]) => [field, before[key]])),
    Object.fromEntries(written.map(([field, key]) => [field, patch[key]]))
  );
};

/** A short machine reason for a failure; never a message or a stack trace. */
export const reasonOf = (error: unknown): string => {
  const code = (error as any)?.code;
  if (typeof code === 'string' && /^[a-z0-9_/-]{1,40}$/i.test(code)) {
    return code.replace(/\//g, '_').toLowerCase();
  }
  const status = (error as any)?.response?.status;
  if (typeof status === 'number') return `http_${status}`;
  return 'error';
};

export const sanitizeDetails = (details: ActivityDetails = {}) => ({
  targets: (details.targets ?? [])
    .filter((t) => t && TARGET_TYPES.includes(t.type) && typeof t.id === 'string' && t.id)
    .slice(0, MAX_QUEUED_TARGETS)
    .map((t) => ({
      type: t.type,
      id: t.id.slice(0, 128),
      ...(t.outcome ? { outcome: t.outcome } : {}),
    })),
  changes: (details.changes ?? [])
    .filter((c, i, all) => CHANGE_FIELDS.includes(c.field) && all.findIndex((o) => o.field === c.field) === i)
    .map((c) => ({
      field: c.field,
      before: loggableValue(c.before),
      after: loggableValue(c.after),
    })),
  reason:
    typeof details.reason === 'string' && /^[a-z0-9_-]{1,40}$/.test(details.reason)
      ? details.reason
      : null,
});

export const profileOf = (initials: string | null | undefined) =>
  text(initials, 12) || null;

// ---- Stored form -----------------------------------------------------------
// Firestore rules cannot iterate lists, and slot-by-slot checks exceed their
// expression limit, so the stored shape is one they check completely in a few
// expressions (firestore.rules isValidSecurityActivity):
//   targets: one string of at most MAX_TARGETS entries "type:id[:outcome]"
//            joined by "|", the last one "more:<n>" when there were more;
//   changes: a map keyed by CHANGE_FIELDS, each { before, after } with a
//            string (≤ 120), number, boolean or null; lists become "1, 2".

export type StoredValue = string | number | boolean | null;

const targetText = (t: ActivityTarget) =>
  `${t.type}:${t.id.replace(/[:|\r\n]/g, '_').slice(0, 128)}${t.outcome ? `:${t.outcome}` : ''}`;

export const encodeTargets = (targets: ActivityTarget[]): string => {
  if (targets.length <= MAX_TARGETS) return targets.map(targetText).join('|');
  const kept = targets.slice(0, MAX_TARGETS - 1).map(targetText);
  return [...kept, `more:${targets.length - kept.length}`].join('|');
};

const storedValue = (value: ActivityValue): StoredValue =>
  Array.isArray(value) ? value.join(', ').slice(0, MAX_STORED_VALUE) : value;

export const encodeChanges = (changes: ActivityChange[]) =>
  Object.fromEntries(
    changes.map((c) => [c.field, { before: storedValue(c.before), after: storedValue(c.after) }])
  ) as Partial<Record<ChangeField, { before: StoredValue; after: StoredValue }>>;

const TARGET_PATTERN = new RegExp(`^(${TARGET_TYPES.join('|')}):([^:|\\r\\n]{1,128})(?::(succeeded|failed))?$`);

/** Reads stored targets defensively: anything not in the stored form is skipped. */
export const decodeTargets = (value: unknown) => {
  const targets: ActivityTarget[] = [];
  let more = 0;
  const entries = typeof value === 'string' && value ? value.split('|').slice(0, MAX_TARGETS) : [];
  for (const entry of entries) {
    const extra = /^more:(\d{1,5})$/.exec(entry);
    if (extra) {
      more = Number(extra[1]);
      continue;
    }
    const match = TARGET_PATTERN.exec(entry);
    if (match) {
      targets.push({
        type: match[1] as ActivityTarget['type'],
        id: match[2],
        ...(match[3] ? { outcome: match[3] as ActivityTarget['outcome'] } : {}),
      });
    }
  }
  return { targets, more };
};

const readValue = (value: unknown): ActivityValue =>
  typeof value === 'string'
    ? value.slice(0, MAX_STORED_VALUE)
    : (typeof value === 'number' && Number.isFinite(value)) || typeof value === 'boolean'
    ? value
    : null;

/** Reads stored changes defensively: only allowlisted fields with plain values. */
export const decodeChanges = (value: unknown): ActivityChange[] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return CHANGE_FIELDS.flatMap((field) => {
    const change = (value as Record<string, unknown>)[field];
    if (!change || typeof change !== 'object') return [];
    const { before, after } = change as Record<string, unknown>;
    return [{ field, before: readValue(before), after: readValue(after) }];
  });
};
